import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "selectAllCheckbox",
    "rowCheckbox",
    "sendButton",
    "sendButtonLabel",
    "confirmDialog",
    "confirmMessage",
    "idsContainer",
  ];

  static values = {
    idParam: { type: String, default: "producer_user_ids[]" },
    rowIdDataset: { type: String, default: "producerUserId" },
  };

  connect() {
    this._onFiltered = this.onFilterApplied.bind(this);
    this.element.addEventListener("manage-invites:filtered", this._onFiltered);
    this.updateButton();
  }

  disconnect() {
    this.element.removeEventListener("manage-invites:filtered", this._onFiltered);
  }

  rowChanged() {
    this.syncSelectAllState();
    this.updateButton();
  }

  toggleSelectAll() {
    const checked = this.selectAllCheckboxTarget.checked;
    this.visibleRemindableRowCheckboxes().forEach((checkbox) => {
      checkbox.checked = checked;
    });
    this.updateButton();
  }

  onFilterApplied() {
    this.syncSelectAllState();
    this.updateButton();
  }

  openConfirmDialog() {
    const count = this.selectedCount();
    if (count === 0) return;

    this.confirmMessageTarget.textContent = this.confirmMessageTemplate.replace(
      "[X]",
      String(count)
    );
    this.confirmDialogTarget.showModal();
  }

  closeConfirmDialog() {
    this.confirmDialogTarget.close();
  }

  prepareForm() {
    this.idsContainerTarget.innerHTML = "";
    this.selectedRowIds().forEach((rowId) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.idParamValue;
      input.value = rowId;
      this.idsContainerTarget.appendChild(input);
    });
  }

  updateButton() {
    const count = this.selectedCount();
    const enabled = count > 0;

    this.sendButtonTarget.disabled = !enabled;
    this.sendButtonLabelTarget.textContent =
      count > 0 ? `${this.sendButtonBaseLabel} (${count})` : this.sendButtonBaseLabel;
  }

  syncSelectAllState() {
    if (!this.hasSelectAllCheckboxTarget) return;

    const visible = this.visibleRemindableRowCheckboxes();
    if (visible.length === 0) {
      this.selectAllCheckboxTarget.checked = false;
      this.selectAllCheckboxTarget.indeterminate = false;
      return;
    }

    const checkedCount = visible.filter((checkbox) => checkbox.checked).length;
    this.selectAllCheckboxTarget.checked = checkedCount === visible.length;
    this.selectAllCheckboxTarget.indeterminate =
      checkedCount > 0 && checkedCount < visible.length;
  }

  selectedCount() {
    return this.selectedRowCheckboxes().length;
  }

  selectedRowIds() {
    return this.selectedRowCheckboxes()
      .map((checkbox) => checkbox.dataset[this.rowIdDatasetValue])
      .filter(Boolean);
  }

  // Rows hidden by a filter keep their tick so widening the filter restores it,
  // but only what's on screen gets reminded.
  selectedRowCheckboxes() {
    return this.visibleRemindableRowCheckboxes().filter((checkbox) => checkbox.checked);
  }

  visibleRemindableRowCheckboxes() {
    return this.rowCheckboxTargets.filter((checkbox) => {
      if (checkbox.disabled) return false;
      const filterRow = checkbox.closest('[data-manage-invites-filters-target="row"]');
      if (filterRow) return !filterRow.hidden;
      return true;
    });
  }

  get sendButtonBaseLabel() {
    return this.sendButtonTarget.dataset.labelDefault || "Send Reminders";
  }

  get confirmMessageTemplate() {
    return (
      this.confirmMessageTarget.dataset.template ||
      "Send a reminder email to [X] invited producers?"
    );
  }
}
